import {AfterContentInit, Component, Input, OnDestroy, OnInit} from '@angular/core';
import {
  DataPresence,
  FieldsNdiStatsDataType,
  FieldsNdiStatsDropDownItem,
  IFieldsNdiStatsData
}                                                              from './fields-ndi-stats.types';
import {BehaviorSubject, Subscription}                         from 'rxjs';
import {
  ApTranslationService
}                                                              from '../../../../ap-utils/service/ap-translation.service';
import {
  IChartSet
}                                                              from '../../../../stores/statistic/statistic.store';
import {
  BoxPlotChartDataItem,
  IBarLineAreaChartData
}                                                              from '../../../../ap-interface';
import {
  N_UPTAKE_LEGEND,
  NDI_LEGEND
}                                                              from '../../../../ap-map/layers/layer.legends';
import {
  ILegendValue
}                                                              from '../../../../stores/map/map.legend.store';
import {
  DiagramForHistogramStatisticsService,
  DiagramTableData,
  DiagramTableStatisticObject
}                                                              from '../../services/diagram-for-histogram-statistics.service';
import {
  GetRoundNumericService
}                                                              from '../../../../ap-utils/service/get-round-numeric.service';
import {Create}                                                from 'ts-tooling';

@Component({
  selector: 'ap-fields-ndi-stats',
  templateUrl: 'fields-ndi-stats.component.html',
  styleUrls: ['fields-ndi-stats.component.scss']
})
export class ApFieldsNdiStatsComponent implements OnInit, AfterContentInit, OnDestroy {
  @Input() public data: IFieldsNdiStatsData[] = [];
  @Input() public title = '';
  @Input() public emptyMessage = '';

  public plotBoxTitle: string;
  public dropdownSelectionItems: FieldsNdiStatsDropDownItem[] = [];
  public selectedDropDownItem = new BehaviorSubject<FieldsNdiStatsDropDownItem | undefined>(undefined);
  public genericBarsData = new BehaviorSubject<IChartSet<IBarLineAreaChartData>[]>([]);
  public plotBoxData = new BehaviorSubject<IChartSet<BoxPlotChartDataItem[]>[]>([]);
  public dataPresence = new BehaviorSubject<DataPresence>({} as DataPresence);

  private _subscriptions: Subscription[] = [];

  constructor(private translationService: ApTranslationService,
              private roundNumericService: GetRoundNumericService,
              private diagramForHistogramStatisticService: DiagramForHistogramStatisticsService) {
  }

  public ngOnInit(): void {
    this.dropdownSelectionItems = this.getItemsForSelectionDropdown();
    this.selectedDropDownItem.next(this.dropdownSelectionItems[1]);
  }

  public ngAfterContentInit(): void {
    this._subscriptions.push(this.selectedDropDownItem.subscribe((selectedItem: FieldsNdiStatsDropDownItem) => {
      const generatedBarsData = this.generateHistogram(selectedItem, this.data);
      const generatedPlotBoxData = this.generateBoxplot(selectedItem, this.data);
      const dataPresence: DataPresence = {
        IsGenericBarsDataPresent: generatedBarsData[0]?.data?.data?.length > 0,
        IsPlotBoxDataPresent: generatedPlotBoxData[0]?.data?.length > 0
      };
      if (dataPresence.IsGenericBarsDataPresent && dataPresence.IsPlotBoxDataPresent) {
        this.setPlotBoxTitle(selectedItem);
        this.genericBarsData.next(generatedBarsData);
        this.plotBoxData.next(generatedPlotBoxData);
        this.emptyMessage = '';
      } else {
        this.emptyMessage = this.getEmptyTitle(selectedItem);
      }
      this.dataPresence.next(dataPresence);
    }));
  }

  public ngOnDestroy(): void {
    this._subscriptions?.forEach(x => x?.unsubscribe());
  }

  /**
   * Generates bar chart configuration for last sampling date per year
   * @param selectedItem
   * @param items the selected items
   * @private
   */
  private generateHistogram(selectedItem: FieldsNdiStatsDropDownItem,
                            items: IFieldsNdiStatsData[]): IChartSet<IBarLineAreaChartData>[] | undefined {
    let nDataTitle = '';
    let legendValues: ILegendValue[] = [];
    let filteredItems: IFieldsNdiStatsData[] = [];
    switch (selectedItem.Id) {
      case FieldsNdiStatsDataType.NIndex:
        nDataTitle = 'Global__NDI_Name';
        legendValues = NDI_LEGEND().values as ILegendValue[];
        filteredItems = items;
        break;
      case FieldsNdiStatsDataType.NUptake:
        nDataTitle = 'NUptake';
        filteredItems = this.getNUptakeItems(items);
        const minAndMax = this.getMinAndMaxFromNUptake(filteredItems);
        legendValues = N_UPTAKE_LEGEND(minAndMax).values as ILegendValue[];
        break;
    }
    if (!legendValues || legendValues?.length < 0) {
      return undefined;
    }
    const selectedArea = filteredItems.reduce((sum, item) => sum + Create(item?.AreaHa, 0), 0);
    const selectedFieldCells = filteredItems.reduce((sum, item) => sum + Create(item?.Cells, 0), 0);
    const diagramStatisticsObject = filteredItems?.map(x => {
      const statisticsObject: DiagramTableStatisticObject = {Histogram: []};
      let histogram: { [key: string]: number } | undefined;
      switch (selectedItem.Id) {
        case FieldsNdiStatsDataType.NIndex:
          histogram = x.NIndexHistogram;
          break;
        case FieldsNdiStatsDataType.NUptake:
          histogram = x.NUptakeHistogram;
          break;
      }
      if (histogram) {
        Object.keys(histogram).forEach(key => {
          const histKey = parseFloat(key);
          if (isNaN(histKey)) {
            return;
          }
          statisticsObject.Histogram.push({Key: histKey, Value: histogram[key]});
        });
      }
      return statisticsObject;
    }) ?? [];
    const diagramTableData: DiagramTableData = {
      TotalArea: selectedArea,
      TotalCells: selectedFieldCells,
      Categories: legendValues,
      AxesTitleX: 'Global__Area',
      AxesTitleY: nDataTitle,
      DiagramTitle: `${this.translationService.translate('Global__Area')} / ${this.translationService.translate(nDataTitle)}`,
      RoundAreaNoteToFarmSettings: false,
      Statistics: diagramStatisticsObject
    };
    return this.diagramForHistogramStatisticService.generateDiagram(diagramTableData);
  }

  /**
   * Generates bar chart configuration for last sampling date per year
   * @param selectedItem
   * @param items the selected items
   * @private
   */
  private generateBoxplot(selectedItem: FieldsNdiStatsDropDownItem, items: IFieldsNdiStatsData[]): IChartSet<BoxPlotChartDataItem[]>[] {
    const boxPlotData: BoxPlotChartDataItem[] = [];
    let nDataTitle = '';
    let filteredItems: IFieldsNdiStatsData[] = [];
    switch (selectedItem.Id) {
      case FieldsNdiStatsDataType.NIndex:
        nDataTitle = 'Global__NDI_Name';
        filteredItems = items;
        break;
      case FieldsNdiStatsDataType.NUptake:
        nDataTitle = 'NUptake';
        filteredItems = this.getNUptakeItems(items);
        break;
    }
    for (const item of filteredItems) {
      let categoryName = item.FieldName;
      if (categoryName?.length > 12) {
        categoryName = `${categoryName.slice(0, 12)}...`;
      }
      let min = 0;
      let max = 0;
      let average = 0;
      switch (selectedItem.Id) {
        case FieldsNdiStatsDataType.NIndex:
          min = item.NIndexMin;
          max = item.NIndexMax;
          average = item.NIndexAvg;
          break;
        case FieldsNdiStatsDataType.NUptake:
          min = item.NUptakeMin;
          max = item.NUptakeMax;
          average = item.NUptakeAvg;
          break;
      }
      const boxPlotItem = new BoxPlotChartDataItem();
      boxPlotItem.category = categoryName;
      boxPlotItem.median = this.roundNumericService.roundAsNumber(average);
      boxPlotItem.lower = this.roundNumericService.roundAsNumber(min);
      boxPlotItem.q1 = this.roundNumericService.roundAsNumber(min);
      boxPlotItem.upper = this.roundNumericService.roundAsNumber(max);
      boxPlotItem.q3 = this.roundNumericService.roundAsNumber(max);
      boxPlotItem.color = '#18CC33';
      boxPlotData.push(boxPlotItem);
    }
    return [{
      axes: {titleX: nDataTitle},
      step: items?.length < 40 ? 1 : Math.ceil(items.length / 40),
      data: boxPlotData
    } as IChartSet<BoxPlotChartDataItem[]>];
  }

  /**
   * Sets the plot box title based on the selected item type
   * @param selectedItem the currently selected dropdown item
   * @private
   */
  private setPlotBoxTitle(selectedItem: FieldsNdiStatsDropDownItem): void {
    let translatedFirstPart: string;
    switch (selectedItem.Id) {
      case FieldsNdiStatsDataType.NIndex:
        translatedFirstPart = this.translationService.translate('Global__NDI_Name');
        break;
      case FieldsNdiStatsDataType.NUptake:
        translatedFirstPart = this.translationService.translate('NUptake');
        break;
    }
    const translatedSecondPart = this.translationService.translate('Global__Field');
    if (!!translatedFirstPart) {
      this.plotBoxTitle = `${translatedFirstPart} / ${translatedSecondPart}`;
    } else {
      this.plotBoxTitle = translatedSecondPart;
    }
  }

  /**
   * Gets empty message title based on selected data type
   * @param selectedItem Currently selected dropdown item
   * @returns Formatted empty message string
   * @private
   */
  private getEmptyTitle(selectedItem: FieldsNdiStatsDropDownItem): string {
    let dataType = '';
    switch (selectedItem.Id) {
      case FieldsNdiStatsDataType.NIndex:
        dataType = this.translationService.translate('Global__NDI_Name');
        break;
      case FieldsNdiStatsDataType.NUptake:
        dataType = this.translationService.translate('NUptake');
        break;
    }
    return `${this.translationService.translate('Nutrients__StatisticsTitle')}\n${dataType}`;
  }

  /**
   * Calculates the minimum and maximum N uptake values from the data
   * @param data array of fields NDI stats data
   * @returns object containing Min and Max values
   * @private
   */
  private getMinAndMaxFromNUptake(data: IFieldsNdiStatsData[]): { Min: number; Max: number } {
    const filteredItems = data.filter(x => Number.isFinite(x.NUptakeMin) && Number.isFinite(x.NUptakeMax));
    const lowestMin = Math.min(...filteredItems.map(item => item.NUptakeMin));
    const highestMax = Math.max(...filteredItems.map(item => item.NUptakeMax));
    return {Min: lowestMin, Max: highestMax};
  }

  /**
   * Filters items to only include those with finite N uptake average values
   * @param data array of fields NDI stats data
   * @returns filtered array of items
   * @private
   */
  private getNUptakeItems(data: IFieldsNdiStatsData[]): IFieldsNdiStatsData[] {
    return data.filter(x => Number.isFinite(x.NUptakeAvg));
  }

  /**
   * Creates dropdown selection items with translated text
   * @returns array of dropdown items for NDI stats selection
   * @private
   */
  private getItemsForSelectionDropdown(): FieldsNdiStatsDropDownItem[] {
    return [
      {
        Id: FieldsNdiStatsDataType.NIndex,
        Text: this.translationService.translate('Global__NDI_Name')
      },
      {
        Id: FieldsNdiStatsDataType.NUptake,
        Text: this.translationService.translate('NUptake')
      }
    ];
  }
}
